import { useTranslation } from 'react-i18next';
import {
  AlloyButton,
  ComponentLoader,
  StickySection
} from 'cat-ecommerce-alloy';
import ProductListSortBy from '../ProductListSortBy';
import ProductListSpecifications from '../ProductListSpecifications/ProductListSpecifications';
import { useDispatch } from 'react-redux';
import { isEmpty } from '@app/utils';
import { STATUS } from '@app/constants/commonConstants';
import PropTypes from 'prop-types';
import { useRef } from 'react';
import { setUnitOfMeasureCookie } from '../UnitOfMeasureToggle/utils';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import { activeFacetsPropTypes } from '@app/components/common/SpecificationFacets/declarations.propTypes';

const ProductListSpecificationsModal = ({
  customspecificationFacets = null,
  widgetId,
  orderBy,
  queryActionParam,
  removeCurrentModal,
  title,
  className,
  isModal,
  bucketModelNumberFacet,
  setExpanded: productCardExpanded,
  detailsStatus,
  showSpecificationsToggle,
  appliedFacets,
  setAppliedFacets,
  showLeftNav,
  batchPartNumbers,
  unitOfMeasure,
  facets,
  activeFacets,
  queryParamsObject,
  filterFacetsByUOM,
  setUrlToPageOneWithFilter,
  isMarketingPage = false,
  filterByEquipment,
  fbeEnabled,
  partNumbers,
  showTotalResultsCount
}) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const defaultFilters = useRef({ unitOfMeasure, orderBy, activeFacets });
  const { hash } = useLocation();
  const { facets: facetsFromHash } = queryString.parse(hash);
  const facetsArray = !Array.isArray(facetsFromHash)
    ? [facetsFromHash]
    : facetsFromHash;
  const isPending = detailsStatus === STATUS.PENDING;
  const showViewResultsNumber = showTotalResultsCount && !isPending;

  const restoreFilters = () => {
    const {
      unitOfMeasure: unitOfMeasurerRef,
      orderBy: orderByRef,
      activeFacets: activeFacetsRef
    } = defaultFilters.current;

    if (
      unitOfMeasurerRef !== unitOfMeasure ||
      orderByRef !== orderBy ||
      activeFacetsRef !== activeFacets
    ) {
      setUrlToPageOneWithFilter({
        sortBy: orderByRef,
        facets: activeFacetsRef
      });
      dispatch(
        queryActionParam(
          {
            unitOfMeasure: unitOfMeasurerRef,
            sortBy: orderByRef,
            activeFacets: activeFacetsRef,
            queryParamsObject
          },
          true
        )
      );
      setUnitOfMeasureCookie(unitOfMeasurerRef);
    }
  };

  const clearHandleClick = () => {
    const {
      orderBy: orderByFromUrl,
      filterByEquipment: filterByEquipmentFromUrl
    } = queryString.parse(hash);
    const orderByProp = orderByFromUrl ? 'orderBy' : 'sortBy';
    dispatch(
      queryActionParam(
        widgetId
          ? {
              widgetId: widgetId,
              data: {
                activeFacets: [],
                pageNumber: 1,
                batchPartNumbers: []
              }
            }
          : { activeFacets: [] },
        true
      )
    );
    if (widgetId) {
      let activeFacetsWithWidgetId = [];
      activeFacets.forEach(j => {
        activeFacetsWithWidgetId.push(`${j.facet}|${widgetId}`);
      });
      const finalUrlFacets = facetsArray
        .filter(facet => !activeFacetsWithWidgetId.includes(facet))
        .map(item => ({ facet: item }));
      setUrlToPageOneWithFilter &&
        setUrlToPageOneWithFilter({
          filterByEquipment: filterByEquipmentFromUrl,
          facets: finalUrlFacets,
          [orderByProp]: orderByFromUrl,
          isMarketingPage: true,
          fbeEnabledValue: fbeEnabled
        });
    } else {
      setUrlToPageOneWithFilter &&
        setUrlToPageOneWithFilter({
          filterByEquipment: filterByEquipmentFromUrl,
          facets: [],
          [orderByProp]: orderByFromUrl
        });
    }
  };

  const handleViewResultsButton = () => {
    /**
     * We need to update the initialActiveFacets with the current
     * active facets in order to close the modal and prevent the
     * setQueryParam action to be called with the initial facets
     * (cancel button functionality)
     */
    if (setAppliedFacets) {
      setAppliedFacets(activeFacets);
    } else if (appliedFacets) {
      appliedFacets.current = activeFacets;
    }
    defaultFilters.current = { unitOfMeasure, orderBy, activeFacets };
    dispatch(productCardExpanded(null));

    removeCurrentModal();
  };

  const handleCancelButton = () => {
    removeCurrentModal();
    !!queryParamsObject && restoreFilters();
  };

  return (
    <>
      {isPending && (
        <ComponentLoader size="small" contentClassName="position-sticky" />
      )}
      <h2 className="exclude-global h2 display-1 mb-4">{title}</h2>
      <ProductListSortBy
        sortByAction={queryActionParam}
        orderBy={orderBy}
        widgetId={widgetId}
        setUrlToPageOneWithFilter={setUrlToPageOneWithFilter}
        filterByEquipment={filterByEquipment}
        isMarketingPage={isMarketingPage}
        partNumbers={partNumbers}
      />
      {!isEmpty(activeFacets) && showLeftNav && (
        <AlloyButton
          buttonType="link"
          className="ps-0 mb-2 text-capitalize"
          onClick={clearHandleClick}
        >
          {t('CLEAR_ALL_FILTERS')}
        </AlloyButton>
      )}
      {showLeftNav && (
        <ProductListSpecifications
          customspecificationFacets={customspecificationFacets}
          className={className}
          isModal={isModal}
          showSpecificationsToggle={showSpecificationsToggle}
          bucketModelNumberFacet={bucketModelNumberFacet}
          facets={facets}
          unitOfMeasure={unitOfMeasure}
          activeFacets={activeFacets}
          queryActionParam={queryActionParam}
          widgetId={widgetId}
          queryParamsObject={queryParamsObject}
          filterFacetsByUOM={filterFacetsByUOM}
          orderBy={orderBy}
          fbeEnabled={fbeEnabled}
        />
      )}
      <StickySection className="align-self-center py-3 align-items-center bg-white z-index-1">
        {showLeftNav && (
          <AlloyButton
            buttonType="secondary"
            className="d-md-none px-0 w-100 mb-2"
            size="large"
            onClick={handleViewResultsButton}
          >
            {t('VIEW_RESULTS')}
          </AlloyButton>
        )}
        <AlloyButton
          buttonType="ghost"
          className="d-md-none px-0 w-100"
          size="large"
          onClick={handleCancelButton}
        >
          {t('CANCEL')}
        </AlloyButton>
      </StickySection>
    </>
  );
};

export default ProductListSpecificationsModal;
ProductListSpecificationsModal.propTypes = {
  appliedFacets: PropTypes.shape({
    current: PropTypes.arrayOf(
      PropTypes.shape({
        count: PropTypes.number,
        facet: PropTypes.string,
        label: PropTypes.string,
        uom: PropTypes.string
      })
    )
  }),
  activeFacets: activeFacetsPropTypes,
  detailsStatus: PropTypes.string,
  batchPartNumbers: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
  removeCurrentModal: PropTypes.func,
  showTotalResultsCount: PropTypes.bool,
  customspecificationFacets: PropTypes.element,
  widgetId: PropTypes.string,
  orderBy: PropTypes.string,
  queryActionParam: PropTypes.func,
  className: PropTypes.string,
  isModal: PropTypes.bool,
  bucketModelNumberFacet: PropTypes.bool,
  setExpanded: PropTypes.func,
  showSpecificationsToggle: PropTypes.bool,
  setAppliedFacets: PropTypes.func,
  showLeftNav: PropTypes.bool,
  unitOfMeasure: PropTypes.string,
  facets: PropTypes.arrayOf(
    PropTypes.shape({
      allValuesReturned: PropTypes.bool,
      allowMultipleValueSelection: PropTypes.bool,
      facetId: PropTypes.string,
      label: PropTypes.string,
      uom: PropTypes.string,
      value: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
          facet: PropTypes.string,
          label: PropTypes.string
        })
      )
    })
  ).isRequired,
  queryParamsObject: PropTypes.func,
  filterFacetsByUOM: PropTypes.func,
  setUrlToPageOneWithFilter: PropTypes.func,
  isMarketingPage: PropTypes.bool,
  filterByEquipment: PropTypes.bool,
  fbeEnabled: PropTypes.bool,
  partNumbers: PropTypes.string
};
